import { EntityListPage, type FilterField, type DataTableColumn, type CardConfig } from '@/components/common'
import { SearchableSelect } from '@/components/common/SearchableSelect'
import { CurrencyInput } from '@/components/ui/CurrencyInput'
import { Modal, ConfirmDialog } from '@/components/ui'
import { useUnit } from '@/contexts/UnitContext'
import { matchesSearch } from '@/lib/search'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { getCompatibleUnits, convertToBase, formatConverted, getUnit } from '@/lib/unitConversions'
import {
  listProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  type Product
} from '@/lib/services/products'
import { listMeasures, type Measure } from '@/lib/services/measures'
import {
  listProductComponents,
  addProductComponent,
  updateProductComponent,
  removeProductComponent,
  type ProductComponent
} from '@/lib/services/productComponents'
import { listComponentCandidates, type ComponentCandidate, updatePricingInput } from '@/lib/services/pricingInputs'

interface ProductFormData {
  name: string
  uom: string
  price: string
}

interface ComponentFormData {
  component_id: string
  qty: string
  uom: string              // usage unit code (from compatible units dropdown)
  applicationsPerQuantity?: number
}

// Map activity keys to component action label
const ACTIVITY_TO_COMPONENT_LABEL: Record<string, string> = {
  alimentos: 'Receita',
  artesanato: 'Componentes',
  metalurgica: 'Componentes',
  construcao: 'Componentes',
  agro: 'Componentes',
  manicure: 'Serviços'
}

function getComponentActionLabel(activity?: string): string {
  if (!activity) return 'Componentes'
  return ACTIVITY_TO_COMPONENT_LABEL[activity] || 'Componentes'
}

export function Products() {
  const { currentUnit } = useUnit()
  const [measures, setMeasures] = useState<Measure[]>([])
  const [measuresLoading, setMeasuresLoading] = useState(true)

  // Modal de componentes
  const [showComponentsModal, setShowComponentsModal] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [components, setComponents] = useState<ProductComponent[]>([])
  const [inputs, setInputs] = useState<ComponentCandidate[]>([])
  const [componentsLoading, setComponentsLoading] = useState(false)
  const [editingComponentId, setEditingComponentId] = useState<string | null>(null)
  const [componentForm, setComponentForm] = useState<ComponentFormData>({ component_id: '', qty: '', uom: '' })
  const [componentFormError, setComponentFormError] = useState<string | null>(null)
  const [savingComponent, setSavingComponent] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [helpDialogOpen, setHelpDialogOpen] = useState(false)
  const [confirmRemovalId, setConfirmRemovalId] = useState<string | null>(null)
  const [hoveredComponentId, setHoveredComponentId] = useState<string | null>(null)
  const [editingPriceId, setEditingPriceId] = useState<string | null>(null)
  const [priceEditValue, setPriceEditValue] = useState('')
  const [batchQty, setBatchQty] = useState<string>('')
  const [savingBatchQty, setSavingBatchQty] = useState(false)

  const unitId = currentUnit?.id
  if (!unitId) return <div className="p-6 text-red-600">Unidade não selecionada</div>

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(max-width: 640px)')
    const update = () => setIsMobile(mq.matches)
    update()
    try { mq.addEventListener('change', update) } catch { mq.addListener(update) }
    return () => { try { mq.removeEventListener('change', update) } catch { mq.removeListener(update) } }
  }, [])

  useEffect(() => {
    listMeasures()
      .then(setMeasures)
      .catch(() => setMeasures([]))
      .finally(() => setMeasuresLoading(false))
  }, [])

  async function saveBatchQty() {
    if (!selectedProduct) return
    const val = parseFloat(batchQty)
    if (batchQty !== '' && (isNaN(val) || val <= 0)) return
    const newVal = batchQty === '' ? undefined : val
    const current = selectedProduct.attrs?.batch_qty
    if (newVal === current) return
    try {
      setSavingBatchQty(true)
      const updated = await updateProduct(selectedProduct.id, {
        attrs: { ...selectedProduct.attrs, batch_qty: newVal }
      })
      setSelectedProduct(updated)
    } catch {
      // silently ignore
    } finally {
      setSavingBatchQty(false)
    }
  }

  // Funções de modal de componentes
  async function openComponentsModal(product: Product) {
    if (!unitId) {
      alert('Unidade não identificada')
      return
    }

    setSelectedProduct(product)
    setBatchQty(product.attrs?.batch_qty != null ? String(product.attrs.batch_qty) : '')
    setShowComponentsModal(true)
    setComponentsLoading(true)
      setComponentForm({ component_id: '', qty: '', uom: '' })
      // Focus search input so user can add another component quickly on mobile
      try {
        const search = document.querySelector<HTMLInputElement>('input[placeholder="Buscar insumo..."]')
        if (search) {
          search.focus()
          search.select()
        }
      } catch (e) {
        // ignore
      }
    setEditingComponentId(null)
    setComponentFormError(null)

    try {
      const [comps, inps] = await Promise.all([
        listProductComponents(product.id),
        listComponentCandidates(unitId)
      ])
      setComponents(comps)
      setInputs(inps)
    } catch (error) {
      console.error('Error loading components:', error)
      setComponents([])
      setInputs([])
    } finally {
      setComponentsLoading(false)
    }
  }

  function closeComponentsModal() {
    setShowComponentsModal(false)
    setSelectedProduct(null)
    setComponents([])
    setInputs([])
    setComponentForm({ component_id: '', qty: '', uom: '' })
    setEditingComponentId(null)
    setComponentFormError(null)
    setConfirmRemovalId(null)
    setHoveredComponentId(null)
    setEditingPriceId(null)
    setPriceEditValue('')
    setBatchQty('')
    setHelpDialogOpen(false)
  }

  function openEditComponent(comp: ProductComponent) {
    // Toggle edit: if clicking the same component that's being edited, cancel edit
    if (editingComponentId === comp.id) {
      setEditingComponentId(null)
      setComponentForm({ component_id: '', qty: '', uom: '' })
      setComponentFormError(null)
      setHoveredComponentId(null)
      return
    }

    setEditingComponentId(comp.id)
    setHoveredComponentId(comp.id)
    setComponentForm({ component_id: comp.component_id, qty: String(comp.qty), uom: comp.uom })
    setComponentFormError(null)
  }

  async function handleSaveComponent() {
    if (!componentForm.component_id) {
      setComponentFormError('Selecione um insumo')
      return
    }
    const qty = parseFloat(componentForm.qty)
    if (!componentForm.qty || isNaN(qty) || qty <= 0) {
      setComponentFormError('Quantidade inválida')
      return
    }
    if (!componentForm.uom) {
      setComponentFormError('Informe a unidade')
      return
    }

    if (!unitId || !selectedProduct) {
      setComponentFormError('Erro: unidade ou produto não identificados')
      return
    }

    try {
      setSavingComponent(true)
      setComponentFormError(null)

      // Get the base UOM from the component (insumo) record
      const componentRecord = inputs.find((i) => i.id === componentForm.component_id)
      const baseUom = componentRecord?.uom ?? componentForm.uom

      // ── Calculate final qty and base consumption (base_qty / base_uom) ─────
      let finalQty = qty
      let finalUom = componentForm.uom
      let baseQtyVal: number | undefined
      let baseUomVal: string | undefined

      // Case 1: User selected a usage unit that differs from the base UOM
      if (componentForm.uom !== baseUom) {
        const qtyInBase = convertToBase(qty, componentForm.uom, baseUom)
        if (qtyInBase === null) {
          setComponentFormError('Não é possível converter entre as unidades selecionadas')
          return
        }

        // If dividing by applications, calculate the converted quantity first
        if (componentForm.applicationsPerQuantity && componentForm.applicationsPerQuantity > 0) {
          const apps = componentForm.applicationsPerQuantity as number
          finalQty = qtyInBase / apps
          finalUom = baseUom
          baseQtyVal = finalQty
          baseUomVal = baseUom
        } else {
          // Record in usage units; base_qty is the converted value
          finalQty = qty
          finalUom = componentForm.uom
          baseQtyVal = qtyInBase
          baseUomVal = baseUom
        }
      } else {
        // Case 2: User selected the base UOM directly
        if (componentForm.applicationsPerQuantity && componentForm.applicationsPerQuantity > 0) {
          const apps = componentForm.applicationsPerQuantity as number
          finalQty = qty / apps
          finalUom = baseUom
          baseQtyVal = finalQty
          baseUomVal = baseUom
        } else {
          // No conversion needed
          finalQty = qty
          finalUom = baseUom
          baseQtyVal = qty
          baseUomVal = baseUom
        }
      }

      if (editingComponentId) {
        await updateProductComponent(editingComponentId, finalQty, finalUom)
        setComponents((prev) =>
          prev.map((c) =>
            c.id === editingComponentId ? { ...c, qty: finalQty, uom: finalUom } : c
          )
        )
      } else {
        const created = await addProductComponent(
          unitId,
          selectedProduct.id,
          componentForm.component_id,
          componentRecord?.source_type ?? 'asset',
          finalQty,
          finalUom,
          baseQtyVal,
          baseUomVal
        )
        setComponents((prev) => [...prev, created])
      }
      setComponentForm({ component_id: '', qty: '', uom: '' })
      setEditingComponentId(null)
    } catch (error: any) {
      setComponentFormError(error.message || 'Erro ao salvar')
    } finally {
      setSavingComponent(false)
    }
  }

  function openHelpDialog() {
    setHelpDialogOpen(true)
  }

  async function handleRemoveComponent(id: string) {
    // open confirm dialog
    setConfirmRemovalId(id)
  }

  async function confirmRemoveComponent() {
    const id = confirmRemovalId
    if (!id) return
    try {
      await removeProductComponent(id)
      setComponents((prev) => prev.filter((c) => c.id !== id))
    } catch (error: any) {
      alert(error.message || 'Erro ao remover')
    } finally {
      setConfirmRemovalId(null)
    }
  }

  function handleInputChange(componentId: string) {
    const inp = inputs.find((i) => i.id === componentId)
    setComponentForm((f) => ({
      ...f,
      component_id: componentId,
      uom: inp?.uom ?? f.uom,
      applicationsPerQuantity: undefined  // reset when changing component
    }))
  }

  const filterFields: FilterField[] = [
    { key: 'name', label: 'Descrição', type: 'text', placeholder: 'Filtrar por descrição...' },
    { key: 'uom', label: 'Unidade', type: 'text', placeholder: 'Filtrar por unidade...' }
  ]

  const tableColumns: DataTableColumn<Product>[] = [
    {
      key: 'name',
      label: 'Descrição',
      render: (p) => <div className="font-medium text-slate-900 dark:text-white">{p.name}</div>
    },
    {
      key: 'uom',
      label: 'Unid. venda',
      render: (p) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200">
          {p.uom}
        </span>
      )
    },
    {
      key: 'batch_qty',
      label: 'Rend./lote',
      render: (p) => (
        <span className="text-slate-700 dark:text-slate-300">
          {p.attrs?.batch_qty ? `${p.attrs.batch_qty} ${p.uom}` : '-'}
        </span>
      )
    },
    {
      key: 'price',
      label: 'Preço venda',
      render: (p) => <span className="text-slate-700 dark:text-slate-300">{p.price ? `R$ ${p.price.toFixed(2)}` : '-'}</span>
    }
  ]

  const config = {
    entityType: 'products',
    filterFields,
    loadData: async () => await listProducts(unitId),
    createEntity: async (data: ProductFormData) => {
      const price = data.price ? parseFloat(data.price) : undefined
      return await createProduct(unitId, { name: data.name.trim(), uom: data.uom.trim(), price })
    },
    updateEntity: async (id: string, data: ProductFormData) => {
      const price = data.price ? parseFloat(data.price) : undefined
      return await updateProduct(id, { name: data.name.trim(), uom: data.uom.trim(), price })
    },
    deleteEntity: async (id: string) => {
      await deleteProduct(id)
    },
    applyFilters: (items: Product[], searchQuery: string, filterValues: any) => {
      return items.filter((item) => {
        if (searchQuery.trim()) {
          if (!matchesSearch(item.name, searchQuery) && !matchesSearch(item.uom, searchQuery)) return false
        }
        if (filterValues.name?.trim() && !matchesSearch(item.name, filterValues.name)) return false
        if (filterValues.uom?.trim() && !matchesSearch(item.uom, filterValues.uom)) return false
        return true
      })
    },
    validateForm: (data: ProductFormData) => {
      const errors: Record<string, string> = {}
      if (!data.name.trim()) errors.name = 'Descrição é obrigatória'
      else if (data.name.trim().length < 2) errors.name = 'Mínimo 2 caracteres'
      if (!data.uom.trim()) errors.uom = 'Unidade de venda é obrigatória'
      if (data.price && isNaN(parseFloat(data.price))) errors.price = 'Preço inválido'
      return errors
    }
  }

  const labels = {
    title: 'Produtos',
    description: 'Catálogo de produtos da unidade',
    newButton: 'Novo Produto',
    searchPlaceholder: 'Buscar produto...',
    emptyTitle: 'Nenhum produto cadastrado',
    emptyDescription: 'Comece adicionando seus primeiros produtos',
    emptyActionLabel: 'Novo Produto',
    modalTitleNew: 'Novo Produto',
    modalTitleEdit: 'Editar Produto',
    modalTitleView: 'Visualizar Produto',
    recordTitle: 'Produto',
    deleteTitle: 'Deletar Produto',
    deleteMessage: 'Tem certeza que deseja deletar este produto? Esta ação não pode ser desfeita.',
    deleteButton: 'Deletar',
    cancelButton: 'Cancelar',
    submitButton: 'Criar Produto',
    updateButton: 'Atualizar Produto',
    viewCard: 'Visualização em Cards',
    viewTable: 'Visualização em Tabela',
    clearSearch: 'Limpar Busca'
  }

  const icon = (
    <svg className="h-7 w-7 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
    </svg>
  )

  const cardConfig: CardConfig<Product> = {
    getIcon: () => (
      <svg className="h-5 w-5 text-slate-700 dark:text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
      </svg>
    ),
    getTitle: (p) => p.name,
    getSubtitle: (p) => `Unidade: ${p.uom}`,
    fields: [
      { key: 'uom', label: 'Unidade de venda', getValue: (p) => p.uom },
      { key: 'batch_qty', label: 'Rendimento/lote', getValue: (p) => p.attrs?.batch_qty ? `${p.attrs.batch_qty} ${p.uom}` : '-' },
      { key: 'price', label: 'Preço de venda', getValue: (p) => (p.price ? `R$ ${p.price.toFixed(2)}` : 'Não definido') }
    ],
    editLabel: 'Editar',
    deleteLabel: 'Deletar',
    getExtraActions: (p) => [
      {
        label: getComponentActionLabel(currentUnit?.attrs?.activity),
        icon: (
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
          </svg>
        ),
        onClick: () => openComponentsModal(p),
        variant: 'info' as const
      }
    ]
  }

  const measureOptions = measures.map((m) => ({ value: m.code, label: m.name, meta: m.symbol }))

  const renderForm = (
    formData: ProductFormData,
    setFormData: (d: ProductFormData) => void,
    formErrors: Record<string, string>,
    submitting: boolean,
    _editingEntity: Product | null,
    mode: 'create' | 'edit' | 'view'
  ) => {
    const disabled = submitting || mode === 'view'
    return (
      <div className="space-y-4">
        {/* Nome */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Descrição *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Ex: Bolo de chocolate, Cadeira artesanal..."
            className={`w-full px-4 py-2 rounded-lg border ${formErrors.name ? 'border-red-300' : 'border-slate-300'} dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500`}
            disabled={disabled}
            autoFocus
          />
          {formErrors.name && <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>}
        </div>

        {/* Unidade de venda */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Unidade de venda *
          </label>
          {measuresLoading ? (
            <div className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-700 text-slate-500 text-sm">
              Carregando unidades...
            </div>
          ) : (
            <SearchableSelect
              options={measureOptions}
              value={formData.uom}
              onChange={(v) => setFormData({ ...formData, uom: v })}
              placeholder="Ex: unidade, dúzia, kg..."
              disabled={disabled}
              error={!!formErrors.uom}
            />
          )}
          {formErrors.uom && <p className="mt-1 text-sm text-red-600">{formErrors.uom}</p>}
        </div>

        {/* Rendimento por lote */}
        {/* Note: rendimento is now managed in the recipe/components modal */}

        {/* Preço de venda */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Preço de venda <span className="text-xs text-slate-400 font-normal">(opcional)</span>
          </label>
          <div className="flex items-center gap-2">
            <span className="text-slate-600 dark:text-slate-400 text-sm">R$</span>
            <CurrencyInput
              value={formData.price}
              onChange={(v) => setFormData({ ...formData, price: v })}
              placeholder="0,00"
              disabled={disabled}
              error={!!formErrors.price}
            />
          </div>
          {formErrors.price && <p className="mt-1 text-sm text-red-600">{formErrors.price}</p>}
        </div>
      </div>
    )
  }

  return (
    <>
      <EntityListPage<Product, ProductFormData>
        config={config}
        labels={labels}
        icon={icon}
        filterFields={filterFields}
        tableColumns={tableColumns}
        cardConfig={cardConfig}
        renderForm={renderForm}
        entityToFormData={(p) => ({
          name: p.name,
          uom: p.uom,
          price: p.price ? String(p.price) : ''
        })}
        emptyFormData={() => ({ name: '', uom: '', price: '' })}
      />

      {/* Modal de Componentes */}
      <Modal
        isOpen={showComponentsModal}
        onClose={closeComponentsModal}
        title={selectedProduct
          ? `${getComponentActionLabel(currentUnit?.attrs?.activity)} — ${selectedProduct.name}`
          : getComponentActionLabel(currentUnit?.attrs?.activity)
        }
        size="md"
        mobileTopAligned={false}
        headerLeft={
          isMobile ? (
            <button
              onClick={closeComponentsModal}
              disabled={savingComponent}
              className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100 transition-colors"
              aria-label="Fechar"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          ) : undefined
        }
        headerRight={
          <div className="flex items-center gap-3">
            <button
              onClick={handleSaveComponent}
              disabled={savingComponent}
              className="p-1.5 rounded hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors text-emerald-600 dark:text-emerald-300 disabled:opacity-40"
              aria-label={editingComponentId ? 'Salvar alterações' : 'Adicionar componente'}
            >
              {savingComponent ? (
                <svg className="animate-spin w-6 h-6" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>

            {!isMobile && (
              <button
                onClick={closeComponentsModal}
                className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white transition-colors"
                aria-label="Fechar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        }
      >
        <div className="space-y-4">
          {/* Loading */}
          {componentsLoading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2">
              <div className="animate-spin h-7 w-7 rounded-full border-[3px] border-blue-500 border-t-transparent" />
              <p className="text-sm text-slate-400">Carregando...</p>
            </div>
          ) : (
            <>
              {/* Rendimento da receita */}
              <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800">
                <svg className="h-4 w-4 text-blue-500 dark:text-blue-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 11h.01M12 11h.01M15 11h.01M4 19h16a2 2 0 002-2V7a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-xs text-blue-700 dark:text-blue-300 shrink-0">Esta receita produz</span>
                <input
                  type="number"
                  min="0.001"
                  step="any"
                  value={batchQty}
                  onChange={(e) => setBatchQty(e.target.value)}
                  onBlur={saveBatchQty}
                  onKeyDown={(e) => { if (e.key === 'Enter') { (e.target as HTMLInputElement).blur() } }}
                  placeholder="—"
                  disabled={savingBatchQty}
                  className="w-20 text-center px-2 py-1 text-sm font-semibold rounded-lg border border-blue-300 dark:border-blue-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                />
                <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                  {selectedProduct?.uom ?? ''}
                </span>
              </div>

              {/* Separador e Formulário de Adição (no topo) */}
              <div className="border-b border-slate-200 dark:border-slate-700 pb-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {editingComponentId ? 'Editando componente' : 'Adicionar componente'}
                  </p>
                  {editingComponentId && (
                    <button
                      onClick={() => {
                        setEditingComponentId(null)
                        setComponentForm({ component_id: '', qty: '', uom: '' })
                        setComponentFormError(null)
                      }}
                      className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                    >
                      Cancelar edição
                    </button>
                  )}
                </div>

                {/* Insumo */}
                <SearchableSelect
                  options={inputs.map((i) => ({
                    value: i.id,
                    label: i.name,
                    meta: i.source_type === 'template' ? `${i.uom} · novo` : i.uom
                  }))}
                  value={componentForm.component_id}
                  onChange={handleInputChange}
                  placeholder="Buscar insumo..."
                  disabled={!!editingComponentId || savingComponent}
                />

                {/* Exibir preço do insumo selecionado */}
                {componentForm.component_id && (
                  (() => {
                    const selectedInput = inputs.find((i) => i.id === componentForm.component_id)
                    const isEditingPrice = editingPriceId === `modal-${componentForm.component_id}`

                    async function doSavePrice() {
                      const newPrice = parseFloat(priceEditValue)
                      if (!selectedInput || isNaN(newPrice) || newPrice < 0) {
                        setEditingPriceId(null)
                        setPriceEditValue('')
                        return
                      }
                      try {
                        await updatePricingInput(selectedInput.id, { price: newPrice })
                        setInputs((prev) =>
                          prev.map((inp) =>
                            inp.id === selectedInput.id ? { ...inp, price: newPrice } : inp
                          )
                        )
                      } catch (err: any) {
                        alert(err.message || 'Erro ao salvar preço')
                      } finally {
                        setEditingPriceId(null)
                        setPriceEditValue('')
                      }
                    }

                    return (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-700">
                        <span className="text-xs text-amber-700 dark:text-amber-300 shrink-0">Preço do insumo:</span>
                        <div className="flex-1" />
                        {isEditingPrice ? (
                          <div className="flex items-center gap-2">
                            <div className="relative">
                              <CurrencyInput
                                value={priceEditValue}
                                onChange={setPriceEditValue}
                                className="w-28 px-2 py-1 text-sm rounded-lg border border-amber-300 dark:border-amber-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') doSavePrice()
                                  if (e.key === 'Escape') { setEditingPriceId(null); setPriceEditValue('') }
                                }}
                                onBlur={doSavePrice}
                                autoFocus
                              />
                              {priceEditValue && (
                                <button
                                  type="button"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => setPriceEditValue('')}
                                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                                  title="Limpar valor"
                                >
                                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              setEditingPriceId(`modal-${componentForm.component_id}`)
                              setPriceEditValue(selectedInput?.price?.toString() ?? '')
                            }}
                            className="flex items-center gap-1.5 px-2 py-1 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-800/40 transition-colors group"
                            title="Clique para editar o preço"
                          >
                            <span className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                              {selectedInput?.price
                                ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(selectedInput.price)
                                : <span className="text-amber-500 dark:text-amber-400 font-normal">Sem preço</span>
                              }
                            </span>
                            <svg className="h-3.5 w-3.5 text-amber-500 dark:text-amber-400 opacity-60 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        )}
                      </div>
                    )
                  })()
                )}

                {/* Qtd + Unidade de uso (select compatível) */}
                {(() => {
                  const baseUom = inputs.find((i) => i.id === componentForm.component_id)?.uom ?? componentForm.uom
                  const compatibleUnits = getCompatibleUnits(baseUom)
                  const hasConversions = compatibleUnits.length > 1
                  const standardUnits = compatibleUnits.filter((u) => !u.isCulinary)
                  const culinaryUnits = compatibleUnits.filter((u) => u.isCulinary)

                  // Conversion preview
                  const qty = parseFloat(componentForm.qty)
                  const converted = !isNaN(qty) && qty > 0 && componentForm.uom && componentForm.uom !== baseUom
                    ? convertToBase(qty, componentForm.uom, baseUom)
                    : null
                  const baseUnit = getUnit(baseUom)

                  return (
                    <>
                      <div className="flex gap-2 items-start">
                        <div className="flex-1 space-y-1">
                          <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Quantidade</label>
                          <input
                            type="number"
                            min="0"
                            step="any"
                            value={componentForm.qty}
                            onChange={(e) => setComponentForm((f) => ({ ...f, qty: e.target.value }))}
                            placeholder="Ex: 200"
                            className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={savingComponent}
                          />
                        </div>
                        <div className="space-y-1" style={{ minWidth: hasConversions ? '9rem' : '5rem' }}>
                          <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Unidade</label>
                          {hasConversions ? (
                            <select
                              value={componentForm.uom}
                              onChange={(e) => setComponentForm((f) => ({ ...f, uom: e.target.value }))}
                              disabled={savingComponent}
                              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                            >
                              {standardUnits.map((u) => (
                                <option key={u.code} value={u.code}>{u.symbol} — {u.name}</option>
                              ))}
                              {culinaryUnits.length > 0 && (
                                <optgroup label="Medidas culinárias">
                                  {culinaryUnits.map((u) => (
                                    <option key={u.code} value={u.code}>{u.symbol} — {u.name}</option>
                                  ))}
                                </optgroup>
                              )}
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={componentForm.uom}
                              onChange={(e) => setComponentForm((f) => ({ ...f, uom: e.target.value }))}
                              placeholder="Unid."
                              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              disabled={savingComponent}
                            />
                          )}
                        </div>
                      </div>

                      {/* Conversion preview */}
                      {converted !== null && baseUnit && (
                        <p className="text-xs text-blue-600 dark:text-blue-400 pl-1">
                          💡 {componentForm.qty} {getUnit(componentForm.uom)?.symbol ?? componentForm.uom} = <strong>{formatConverted(converted)} {baseUnit.symbol}</strong>
                        </p>
                      )}

                      {/* Division by applications preview */}
                      {componentForm.applicationsPerQuantity && componentForm.applicationsPerQuantity > 0 && baseUnit && (
                        (() => {
                          const baseQty = converted !== null ? converted : (qty > 0 ? qty : null)
                          if (baseQty === null || baseQty === 0) return null
                          const perApplication = baseQty / componentForm.applicationsPerQuantity
                          return (
                            <p className="text-xs text-purple-600 dark:text-purple-400 pl-1">
                              📊 <strong>{formatConverted(baseQty)} {baseUnit.symbol}</strong> ÷ {componentForm.applicationsPerQuantity} = <strong>{formatConverted(perApplication)} {baseUnit.symbol}</strong> por aplicação
                            </p>
                          )
                        })()
                      )}
                    </>
                  )
                })()}

                {/* Link de ajuda */}
                <button
                  type="button"
                  onClick={openHelpDialog}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline text-left transition-colors"
                  disabled={savingComponent}
                >
                  Não sei como colocar a quantidade usada, preciso de ajuda
                </button>

                {componentFormError && (
                  <p className="text-xs text-red-500 dark:text-red-400">{componentFormError}</p>
                )}
              </div>

              {/* Lista de Componentes Adicionados (embaixo) */}
              <div className="border-t border-slate-200 dark:border-slate-700 pt-4 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Componentes Adicionados
                </p>
                {components.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 text-center">
                    <div className="p-4 rounded-full bg-slate-100 dark:bg-slate-800 mb-3">
                      <svg className="h-8 w-8 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <p className="font-medium text-slate-600 dark:text-slate-300 text-sm">Nenhum componente ainda</p>
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Adicione insumos usando o formulário acima</p>
                  </div>
                ) : (
                  <div className="space-y-1 max-h-60 overflow-y-auto pr-2">
                    {components.map((comp) => {
                      const isEditing = editingComponentId === comp.id
                      const isHovered = hoveredComponentId === comp.id
                      return (
                        <div
                          key={comp.id}
                          onMouseEnter={() => setHoveredComponentId(comp.id)}
                          onMouseLeave={() => setHoveredComponentId(null)}
                          onClick={() => {
                            // focus hovered state on mobile (and cancel other edits)
                            setHoveredComponentId(comp.id)
                            if (editingComponentId && editingComponentId !== comp.id) {
                              // clicking another item cancels current edit
                              setEditingComponentId(null)
                              setComponentForm({ component_id: '', qty: '', uom: '' })
                              setComponentFormError(null)
                            }
                          }}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer ${
                            isEditing
                              ? 'bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-200 dark:ring-blue-700 ring-inset'
                                : isHovered
                                  ? 'bg-slate-50 dark:bg-slate-800/50 ring-1 ring-blue-200 dark:ring-blue-700 ring-inset'
                                : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${isEditing || isHovered ? 'text-blue-700 dark:text-blue-300' : 'text-slate-800 dark:text-white'}`}>
                              {comp.component_name ?? comp.component_id}
                            </p>
                            <div className="flex items-center gap-3 mt-0.5">
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                {comp.qty} {comp.uom}
                              </p>
                              {comp.component_price && (
                                <>
                                  <span className="text-xs text-slate-400 dark:text-slate-500">·</span>
                                  <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
                                    R$ {(comp.qty * comp.component_price).toFixed(2)}
                                  </p>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-0.5 shrink-0">
                            <button
                              onClick={() => openEditComponent(comp)}
                              className={`p-1.5 rounded-lg transition-colors ${
                                isEditing
                                  ? 'text-blue-500 bg-blue-100 dark:bg-blue-800/40'
                                  : 'text-slate-400 hover:text-blue-600 hover:bg-slate-100 dark:hover:bg-slate-700'
                              }`}
                              title="Editar"
                            >
                              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleRemoveComponent(comp.id)}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                              title="Remover"
                            >
                              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Help Dialog - renderizado no body via portal */}
      {helpDialogOpen && createPortal(
        <div className="fixed inset-0 z-[10001] bg-black/50 dark:bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-slate-800 px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white">Como calcular a quantidade a usar?</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Duas formas de informar o uso do insumo</p>
              </div>
              <button
                onClick={() => setHelpDialogOpen(false)}
                className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Opção 1 */}
              <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="font-semibold text-blue-900 dark:text-blue-200 text-sm mb-2">✅ Opção 1: Sei exatamente quanto uso</p>
                {(() => {
                  const activity = currentUnit?.attrs?.activity
                  if (activity === 'alimentos') return (
                    <>
                      <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
                        Informe a quantidade real da receita. Use xícaras, colheres, gramas, ml — o sistema converte automaticamente.
                      </p>
                      <div className="text-xs text-blue-700 dark:text-blue-400 bg-white dark:bg-slate-700 p-3 rounded border border-blue-100 dark:border-blue-700 space-y-1">
                        <p className="font-medium">Exemplo (bolo):</p>
                        <p>Farinha de trigo → <strong>2 xícaras</strong> (≈ 240 g)</p>
                        <p>Manteiga → <strong>200 g</strong></p>
                        <p>Essência de baunilha → <strong>1 col chá</strong> (≈ 5 ml)</p>
                      </div>
                    </>
                  )
                  if (activity === 'agro') return (
                    <>
                      <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
                        Informe a quantidade real usada por lote/animal. Use kg, g, colheres, xícaras — o sistema converte para a unidade base.
                      </p>
                      <div className="text-xs text-blue-700 dark:text-blue-400 bg-white dark:bg-slate-700 p-3 rounded border border-blue-100 dark:border-blue-700 space-y-1">
                        <p className="font-medium">Exemplo (ração):</p>
                        <p>Milho moído → <strong>2 kg</strong> por animal/dia</p>
                        <p>Suplemento mineral → <strong>2 col sopa</strong> (≈ 30 g)</p>
                        <p>Sal grosso → <strong>1 pitada</strong> (≈ 0,3 g)</p>
                      </div>
                    </>
                  )
                  if (activity === 'manicure') return (
                    <>
                      <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
                        Informe o quanto você usa por aplicação/atendimento.
                      </p>
                      <div className="text-xs text-blue-700 dark:text-blue-400 bg-white dark:bg-slate-700 p-3 rounded border border-blue-100 dark:border-blue-700 space-y-1">
                        <p className="font-medium">Exemplo:</p>
                        <p>Base → <strong>5 ml</strong> por aplicação</p>
                        <p>Acetona → <strong>10 ml</strong> por remoção</p>
                      </div>
                    </>
                  )
                  if (activity === 'artesanato' || activity === 'construcao' || activity === 'metalurgica') return (
                    <>
                      <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
                        Informe o quanto você usa por peça/unidade produzida.
                      </p>
                      <div className="text-xs text-blue-700 dark:text-blue-400 bg-white dark:bg-slate-700 p-3 rounded border border-blue-100 dark:border-blue-700 space-y-1">
                        <p className="font-medium">Exemplo:</p>
                        <p>Cola → <strong>50 g</strong> por peça</p>
                        <p>Tinta → <strong>30 ml</strong> por peça</p>
                        <p>Fio de cobre → <strong>20 cm</strong> por unidade</p>
                      </div>
                    </>
                  )
                  return (
                    <>
                      <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
                        Informe a quantidade real usada em cada produto/lote.
                      </p>
                      <div className="text-xs text-blue-700 dark:text-blue-400 bg-white dark:bg-slate-700 p-3 rounded border border-blue-100 dark:border-blue-700 space-y-1">
                        <p className="font-medium">Exemplo:</p>
                        <p>Insumo A → <strong>200 g</strong> por produto</p>
                        <p>Insumo B → <strong>2 xícaras</strong> por lote</p>
                      </div>
                    </>
                  )
                })()}
              </div>

              {/* Opção 2 */}
              <div className="p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                <p className="font-semibold text-amber-900 dark:text-amber-200 text-sm mb-2">💡 Opção 2: Não sei a quantidade exata, mas sei quantas vezes uso</p>
                {(() => {
                  const activity = currentUnit?.attrs?.activity
                  if (activity === 'alimentos') return (
                    <>
                      <p className="text-sm text-amber-800 dark:text-amber-300 mb-2">
                        Informe <strong>1</strong> na quantidade e a unidade de compra, e use o campo <em>Divisão por aplicações</em> para dizer quantos lotes você consegue fazer.
                      </p>
                      <div className="text-xs text-amber-700 dark:text-amber-400 bg-white dark:bg-slate-700 p-3 rounded border border-amber-100 dark:border-amber-700 space-y-1">
                        <p className="font-medium">Exemplo:</p>
                        <p>Compro 1 frasco de essência de baunilha</p>
                        <p>Consigo fazer <strong>30 bolos</strong> com ele</p>
                        <p className="mt-1 font-medium">→ Qtd: <strong>1 frasco</strong> · Aplicações: <strong>30</strong></p>
                      </div>
                    </>
                  )
                  if (activity === 'agro') return (
                    <>
                      <p className="text-sm text-amber-800 dark:text-amber-300 mb-2">
                        Informe <strong>1</strong> na quantidade (ex: 1 saca) e quantos lotes/animais aquela quantidade atende no campo <em>Divisão por aplicações</em>.
                      </p>
                      <div className="text-xs text-amber-700 dark:text-amber-400 bg-white dark:bg-slate-700 p-3 rounded border border-amber-100 dark:border-amber-700 space-y-1">
                        <p className="font-medium">Exemplo:</p>
                        <p>Compro 1 saca de sal mineral (30 kg)</p>
                        <p>Uso em <strong>60 rações diárias</strong></p>
                        <p className="mt-1 font-medium">→ Qtd: <strong>1 saca</strong> · Aplicações: <strong>60</strong></p>
                      </div>
                    </>
                  )
                  if (activity === 'manicure') return (
                    <>
                      <p className="text-sm text-amber-800 dark:text-amber-300 mb-2">
                        Informe <strong>1</strong> na quantidade e quantas aplicações você tira de 1 unidade.
                      </p>
                      <div className="text-xs text-amber-700 dark:text-amber-400 bg-white dark:bg-slate-700 p-3 rounded border border-amber-100 dark:border-amber-700 space-y-1">
                        <p className="font-medium">Exemplo:</p>
                        <p>Compro 1 frasco de esmalte (10 ml)</p>
                        <p>Faço <strong>20 aplicações</strong> com ele</p>
                        <p className="mt-1 font-medium">→ Qtd: <strong>1 frasco</strong> · Aplicações: <strong>20</strong></p>
                      </div>
                    </>
                  )
                  if (activity === 'artesanato' || activity === 'construcao' || activity === 'metalurgica') return (
                    <>
                      <p className="text-sm text-amber-800 dark:text-amber-300 mb-2">
                        Informe <strong>1</strong> na quantidade e quantas peças você faz com 1 unidade do insumo.
                      </p>
                      <div className="text-xs text-amber-700 dark:text-amber-400 bg-white dark:bg-slate-700 p-3 rounded border border-amber-100 dark:border-amber-700 space-y-1">
                        <p className="font-medium">Exemplo:</p>
                        <p>Compro 1 tubo de cola</p>
                        <p>Faço <strong>25 peças</strong> com ele</p>
                        <p className="mt-1 font-medium">→ Qtd: <strong>1 un</strong> · Aplicações: <strong>25</strong></p>
                      </div>
                    </>
                  )
                  return (
                    <>
                      <p className="text-sm text-amber-800 dark:text-amber-300 mb-2">
                        Informe <strong>1</strong> na quantidade e quantos produtos você consegue fazer com 1 unidade.
                      </p>
                      <div className="text-xs text-amber-700 dark:text-amber-400 bg-white dark:bg-slate-700 p-3 rounded border border-amber-100 dark:border-amber-700 space-y-1">
                        <p className="font-medium">Exemplo:</p>
                        <p>Compro 1 unidade do insumo</p>
                        <p>Uso em <strong>20 produtos</strong></p>
                        <p className="mt-1 font-medium">→ Qtd: <strong>1 un</strong> · Aplicações: <strong>20</strong></p>
                      </div>
                    </>
                  )
                })()}
              </div>

              {/* Campo de Divisão por Aplicações */}
              <div className="p-4 bg-purple-50 dark:bg-purple-950/30 rounded-lg border border-purple-200 dark:border-purple-800 space-y-3">
                <div>
                  <label className="text-xs font-semibold text-purple-900 dark:text-purple-200">
                    📊 Está usando Opção 2? Informe aqui:
                  </label>
                  <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">
                    <strong>1º:</strong> Feche este modal e preencha a <strong>Quantidade</strong> no formulário (ex: 1 kg)<br/>
                    <strong>2º:</strong> Volte nesta ajuda e informe quantas vezes você usa essa quantidade
                  </p>
                </div>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={componentForm.applicationsPerQuantity || ''}
                  onChange={(e) => setComponentForm((f) => ({ ...f, applicationsPerQuantity: e.target.value ? parseInt(e.target.value) : undefined }))}
                  placeholder="Ex: 20 aplicações/usos com 1 unidade"
                  className="w-full px-3 py-2 text-sm rounded-lg border border-purple-300 dark:border-purple-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                {componentForm.applicationsPerQuantity && componentForm.applicationsPerQuantity > 0 && (
                  <p className="text-xs text-purple-700 dark:text-purple-300 font-medium">
                    ✅ Configurado: <strong>{componentForm.applicationsPerQuantity} aplicações/usos</strong>
                  </p>
                )}
              </div>

              <p className="text-xs text-slate-500 dark:text-slate-400">
                💡 Dica: xícaras e colheres funcionam tanto para líquidos (L) quanto para sólidos (kg). O sistema já converte automaticamente!
              </p>
            </div>

            <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700">
              <button
                onClick={() => setHelpDialogOpen(false)}
                className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
              >
                Entendi!
              </button>
            </div>
          </div>
        </div>
      , document.body)}
      {/* Confirm removal dialog for components */}
      <ConfirmDialog
        isOpen={!!confirmRemovalId}
        onClose={() => setConfirmRemovalId(null)}
        onConfirm={confirmRemoveComponent}
        title="Remover componente"
        message="Tem certeza que deseja remover este componente?"
        confirmText="Remover"
        cancelText="Cancelar"
        type="danger"
      />
    </>
  )
}

